import { z } from "zod";
import type {
  Collaborator, Comment, Filter, FocusSession, Habit, HabitCheckin, Label, Membership, Project, Reminder, Section, Task, User,
} from "./entities.js";

/**
 * Sync protocol, modelled on Todoist's Sync API.
 * The client sends a batch of commands plus the last sync token it holds.
 * The server applies the commands transactionally, then returns every change
 * since that token (or everything, when the token is "*").
 */

const id = z.string().min(1).max(64); // real ids are UUIDs; temp ids are client-chosen
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const dueSchema = z.object({
  date: isoDate,
  datetime: z.string().nullable(),
  timezone: z.string().nullable(),
  string: z.string(),
  isRecurring: z.boolean(),
});
const prioritySchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
const viewStyle = z.enum(["list", "board", "calendar"]);
const color = z.string().max(32);
const attachment = z.object({ fileId: z.string().uuid(), name: z.string().max(255), size: z.number().int().nonnegative(), mime: z.string().max(255) });
const orderItems = z.array(z.object({
  id, childOrder: z.number().int(), parentId: id.nullable().optional(), sectionId: id.nullable().optional(),
})).min(1).max(500);

export const commandArgs = {
  project_add: z.object({
    id, name: z.string().min(1).max(200), color: color.optional(),
    parentId: id.nullable().optional(), childOrder: z.number().int().optional(),
    isFavorite: z.boolean().optional(), viewStyle: viewStyle.optional(),
  }),
  project_update: z.object({
    id, name: z.string().min(1).max(200).optional(), color: color.optional(),
    isFavorite: z.boolean().optional(), viewStyle: viewStyle.optional(),
    isArchived: z.boolean().optional(), parentId: id.nullable().optional(), childOrder: z.number().int().optional(),
  }),
  project_reorder: z.object({ items: z.array(z.object({ id, childOrder: z.number().int(), parentId: id.nullable().optional() })).min(1).max(500) }),
  project_delete: z.object({ id }),
  project_restore: z.object({ id }),
  project_share: z.object({ projectId: id, userId: id }),
  project_unshare: z.object({ projectId: id, userId: id }),
  section_add: z.object({ id, projectId: id, name: z.string().min(1).max(200), sectionOrder: z.number().int().optional() }),
  section_update: z.object({ id, name: z.string().min(1).max(200).optional(), sectionOrder: z.number().int().optional(), isArchived: z.boolean().optional() }),
  section_delete: z.object({ id }),
  item_add: z.object({
    id, projectId: id, sectionId: id.nullable().optional(), parentId: id.nullable().optional(),
    content: z.string().min(1).max(2000), description: z.string().max(16000).optional(),
    priority: prioritySchema.optional(), due: dueSchema.nullable().optional(), deadline: isoDate.nullable().optional(),
    durationMinutes: z.number().int().positive().nullable().optional(), labelIds: z.array(id).optional(),
    childOrder: z.number().int().optional(), assigneeId: id.nullable().optional(),
  }),
  item_update: z.object({
    id, content: z.string().min(1).max(2000).optional(), description: z.string().max(16000).optional(),
    priority: prioritySchema.optional(), due: dueSchema.nullable().optional(), deadline: isoDate.nullable().optional(),
    durationMinutes: z.number().int().positive().nullable().optional(), labelIds: z.array(id).optional(),
    assigneeId: id.nullable().optional(), isCollapsed: z.boolean().optional(), dayOrder: z.number().int().optional(),
  }),
  item_move: z.object({ id, projectId: id.optional(), sectionId: id.nullable().optional(), parentId: id.nullable().optional(), childOrder: z.number().int().optional() }),
  item_reorder: z.object({ items: orderItems }),
  item_complete: z.object({ id, completedAt: z.string().optional() }),
  item_uncomplete: z.object({ id }),
  item_delete: z.object({ id }),
  item_restore: z.object({ id }),
  label_add: z.object({ id, name: z.string().min(1).max(100), color: color.optional(), itemOrder: z.number().int().optional(), isFavorite: z.boolean().optional() }),
  label_update: z.object({ id, name: z.string().min(1).max(100).optional(), color: color.optional(), itemOrder: z.number().int().optional(), isFavorite: z.boolean().optional() }),
  label_delete: z.object({ id }),
  note_add: z.object({ id, taskId: id, content: z.string().max(16000), attachment: attachment.nullable().optional() }),
  note_update: z.object({ id, content: z.string().max(16000) }),
  note_delete: z.object({ id }),
  filter_add: z.object({ id, name: z.string().min(1).max(100), query: z.string().min(1).max(1000), color: color.optional(), itemOrder: z.number().int().optional(), isFavorite: z.boolean().optional() }),
  filter_update: z.object({ id, name: z.string().min(1).max(100).optional(), query: z.string().min(1).max(1000).optional(), color: color.optional(), itemOrder: z.number().int().optional(), isFavorite: z.boolean().optional() }),
  filter_delete: z.object({ id }),
  reminder_add: z.object({ id, taskId: id, type: z.enum(["absolute", "relative"]), at: z.string().nullable().optional(), minuteOffset: z.number().int().min(0).max(60 * 24 * 14).nullable().optional() }),
  reminder_delete: z.object({ id }),
  habit_add: z.object({ id, name: z.string().min(1).max(100), color: color.optional(), icon: z.string().max(16).optional(), targetPerWeek: z.number().int().min(1).max(7).optional(), itemOrder: z.number().int().optional(), reminderTime: hhmm.nullable().optional() }),
  habit_update: z.object({ id, name: z.string().min(1).max(100).optional(), color: color.optional(), icon: z.string().max(16).optional(), targetPerWeek: z.number().int().min(1).max(7).optional(), itemOrder: z.number().int().optional(), isArchived: z.boolean().optional(), reminderTime: hhmm.nullable().optional() }),
  habit_delete: z.object({ id }),
  habit_checkin: z.object({ id, habitId: id, date: isoDate }),
  habit_uncheck: z.object({ habitId: id, date: isoDate }),
  focus_add: z.object({ id, taskId: id.nullable().optional(), kind: z.enum(["pomodoro", "stopwatch"]), startedAt: z.string(), endedAt: z.string(), durationSec: z.number().int().min(1).max(60 * 60 * 12) }),
  focus_delete: z.object({ id }),
  user_update: z.object({
    name: z.string().min(1).max(100).optional(), timezone: z.string().max(64).optional(),
    dailyGoal: z.number().int().min(0).max(100).optional(), weeklyGoal: z.number().int().min(0).max(700).optional(),
    vacationMode: z.boolean().optional(), startOfWeek: z.number().int().min(0).max(6).optional(),
    theme: z.enum(["system", "light", "dark"]).optional(),
    autoReminderMinutes: z.number().int().min(0).max(1440).nullable().optional(),
    dailyDigestTime: hhmm.nullable().optional(),
  }),
} as const;

export type CommandType = keyof typeof commandArgs;
export const COMMAND_TYPES = Object.keys(commandArgs) as CommandType[];

export type Command = {
  [K in CommandType]: { type: K; uuid: string; args: z.infer<(typeof commandArgs)[K]> };
}[CommandType];

export type CommandArgs<K extends CommandType> = z.infer<(typeof commandArgs)[K]>;

/** Loose envelope; args are validated per type by `parseCommand`. */
export const commandEnvelopeSchema = z.object({
  type: z.enum(COMMAND_TYPES as [CommandType, ...CommandType[]]),
  uuid: z.string().uuid(),
  args: z.unknown(),
});

export function parseCommand(raw: unknown): Command {
  const env = commandEnvelopeSchema.parse(raw);
  const args = commandArgs[env.type].parse(env.args);
  return { type: env.type, uuid: env.uuid, args } as Command;
}

export const syncRequestSchema = z.object({
  syncToken: z.string().default("*"),
  commands: z.array(commandEnvelopeSchema).max(100).default([]),
});
export type SyncRequest = z.infer<typeof syncRequestSchema>;

export interface SyncResponse {
  syncToken: string;
  fullSync: boolean;
  user: User;
  projects: Project[];
  sections: Section[];
  tasks: Task[];
  labels: Label[];
  comments: Comment[];
  filters: Filter[];
  reminders: Reminder[];
  habits: Habit[];
  habitCheckins: HabitCheckin[];
  focusSessions: FocusSession[];
  /** Always the full list: everyone in the workspace */
  collaborators: Collaborator[];
  /** Always the full list for projects the user can see */
  memberships: Membership[];
  /** client temp id -> server id for newly created entities */
  tempIdMapping: Record<string, string>;
  /** command uuid -> "ok" | { error } */
  syncStatus: Record<string, "ok" | { error: string }>;
}

// ---------------------------------------------------------------------------
// Non-sync API shapes
// ---------------------------------------------------------------------------
export interface ActivityEvent {
  id: number;
  userId: string;
  projectId: string | null;
  /** "agenda" rows record an agent reading the user's agenda (objectId = the date read). */
  objectType: "task" | "project" | "section" | "comment" | "agenda";
  objectId: string;
  event: "added" | "updated" | "completed" | "uncompleted" | "deleted" | "restored" | "moved" | "commented" | "shared" | "unshared" | "viewed";
  /** `via` names the agent (e.g. "Hermes") when the change came through an agent key. */
  extra: { content?: string; projectName?: string; assigneeId?: string | null; via?: string } | null;
  createdAt: string;
}

export interface Stats {
  completedToday: number;
  completedThisWeek: number;
  dailyGoal: number;
  weeklyGoal: number;
  dailyStreak: { current: number; best: number };
  weeklyStreak: { current: number; best: number };
  karma: number;
  karmaLevel: string;
  totalCompleted: number;
  /** last 28 days, oldest first */
  days: { date: string; count: number }[];
  /** last 28 days by project */
  byProject: { projectId: string; count: number }[];
  focusMinutesToday: number;
  focusMinutesWeek: number;
}

export interface TrashItem {
  type: "task" | "project";
  id: string;
  content: string;
  projectId: string;
  deletedAt: string;
}

// ---------------------------------------------------------------------------
// Agent keys: limited-permission tokens for AI agents and automations
// ---------------------------------------------------------------------------
/**
 * create = add tasks (and see project, section and label names); agenda = see the user's own tasks for a
 * given day (no teammates' tasks, no descriptions, never @private); read = search all visible tasks;
 * write = change, complete or delete.
 */
export type AgentScope = "create" | "agenda" | "read" | "write";
export type AgentAccess = "add" | "add_agenda" | "add_read" | "full";
/** Tasks with this label are never shown to AI agents. */
export const PRIVATE_LABEL = "private";
export const AGENT_ACCESS: Record<AgentAccess, { label: string; description: string; scopes: AgentScope[] }> = {
  add: { label: "Add tasks", description: "Can add tasks and see project, section and label names. Can't see or change existing tasks.", scopes: ["create"] },
  add_agenda: { label: "Add and see my agenda", description: "Can add tasks and see your own tasks for a given day: titles, times, priority and project. Never teammates' tasks, descriptions, comments or anything labelled @private.", scopes: ["create", "agenda"] },
  add_read: { label: "Add and read", description: "Can add tasks and look up existing ones. Can't change, complete or delete anything.", scopes: ["create", "agenda", "read"] },
  full: { label: "Full access", description: "Can add, find, change, complete and delete tasks.", scopes: ["create", "agenda", "read", "write"] },
};

export interface AgentKey {
  id: string;
  name: string;
  access: AgentAccess;
  createdAt: string;
  lastUsedAt: string | null;
}

